import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, X, ExternalLink, ShieldCheck } from "lucide-react";
import api from "@/lib/api";

const SEV = { high: "#FF003C", medium: "#FFD700", good: "#39FF14" };
const KIND_LABEL = {
    channel_verified: "Verificación aprobada",
    low_retention: "Alerta temprana",
    worker_down: "Sistema · proceso detenido",
    upload_reauth: "Reconexión necesaria",
    upload_failed: "Subida fallida",
};

export default function AlertsBanner() {
    const qc = useQueryClient();
    const { data: alerts = [] } = useQuery({
        queryKey: ["alerts"],
        queryFn: async () => (await api.get("/alerts")).data,
        refetchInterval: 60000,
    });
    const dismiss = useMutation({
        mutationFn: async (id) => (await api.post(`/alerts/${id}/read`)).data,
        onSuccess: () => qc.invalidateQueries({ queryKey: ["alerts"] }),
    });

    const unread = alerts.filter((a) => !a.read);
    if (!unread.length) return null;

    return (
        <div className="mb-6 space-y-2" data-testid="alerts-banner">
            {unread.map((a) => {
                const verified = a.kind === "channel_verified";
                const c = verified ? SEV.good : SEV[a.severity] || "#FFD700";
                const Icon = verified ? ShieldCheck : AlertTriangle;
                const label = KIND_LABEL[a.kind] || "Alerta";
                return (
                    <div
                        key={a.id}
                        data-testid={`alert-${a.id}`}
                        className="flex items-start gap-3 border bg-card p-3"
                        style={{ borderColor: c }}
                    >
                        <Icon size={16} className="mt-0.5 shrink-0" style={{ color: c }} />
                        <div className="min-w-0 flex-1">
                            <div className="text-[10px] uppercase tracking-widest" style={{ color: c }}>
                                {label}{a.channel_name ? ` · ${a.channel_name}` : ""}
                            </div>
                            <p className="mt-0.5 text-sm text-foreground">{a.message}</p>
                            <div className="mt-1 flex items-center gap-3 text-[11px]">
                                {a.youtube_url && (
                                    <a
                                        href={a.youtube_url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="flex items-center gap-1 text-primary hover:underline"
                                    >
                                        <ExternalLink size={11} /> {verified ? "Abrir YouTube Studio" : "Ver en YouTube"}
                                    </a>
                                )}
                                {a.kind === "low_retention" && (a.auto_rewrite ? (
                                    <span className="text-emerald-400" data-testid={`alert-auto-${a.id}`}>
                                        ✓ Reescritura automática lanzada
                                    </span>
                                ) : (
                                    <span className="text-muted-foreground">Reescribe el gancho en Retención →</span>
                                ))}
                                {a.kind === "upload_reauth" && (
                                    <a href="/integrations" className="text-primary hover:underline">Reconectar canal →</a>
                                )}
                            </div>
                        </div>
                        <button
                            data-testid={`alert-dismiss-${a.id}`}
                            onClick={() => dismiss.mutate(a.id)}
                            title="Descartar"
                            className="shrink-0 text-muted-foreground transition-colors hover:text-foreground"
                        >
                            <X size={16} />
                        </button>
                    </div>
                );
            })}
        </div>
    );
}
